(() => {
  // ===== CONFIG =====
  const DEBUG = false;

  const TOKEN = window.KMR_STOREFRONT_TOKEN;
  const BADGE_URL = window.KMR_SALE_BADGE_URL;

  const API_VERSION = "2026-01";
  const METAFIELD_NAMESPACE = "custom";
  const METAFIELD_KEY = "compare_at_price";
  const VAT_MULTIPLIER = 1.2;

  if (!TOKEN || !BADGE_URL) {
    if (DEBUG) console.warn("[KMR ISW] Missing TOKEN or BADGE_URL", { TOKEN: !!TOKEN, BADGE_URL });
    return;
  }

  // ===== HELPERS =====

  const toNumber = (v) => {
    if (v == null) return null;
    const s = String(v).trim().replace(/,/g, "");
    const cleaned = s.replace(/[^0-9.]/g, "");
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : null;
  };

  const formatMoney = (pence) => {
    if (window.Shopify?.formatMoney) return Shopify.formatMoney(pence, Shopify.money_format);
    return "£" + (pence / 100).toFixed(2);
  };

  const getCurrentIncVatPricePence = (card) => {
    // Try to find the “main” price line (NOT the Ex. VAT line)
    const candidates = [...card.querySelectorAll("span, div, p")]
      .filter(el => /£\s*\d/.test(el.textContent || ""))
      .filter(el => !/ex\.?\s*vat/i.test(el.textContent || ""))
      .filter(el => !el.classList.contains("kmr-isw-compare-at")); // ignore our injected was-price

    const el = candidates[0];
    if (!el) return null;

    const n = toNumber(el.textContent);
    if (!n || n <= 0) return null;

    return Math.round(n * 100);
  };

  const getHandleFromHref = (href) => {
    const m = String(href || "").match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  };

  const isInBoostContainer = (el) => {
    let n = el;
    for (let i = 0; i < 10 && n; i++) {
      const cls = (n.className || "").toString();
      const id = (n.id || "").toString();
      if (/boost-pfs|boost|instant|suggest/i.test(cls) || /boost|pfs|instant|suggest/i.test(id)) return true;
      n = n.parentElement;
    }
    return false;
  };

  const findCardForLink = (a) => {
    return (
      a.closest(".boost-pfs-instant-search-product-item") ||
      a.closest(".boost-pfs-filter-product-item") ||
      a.closest(".boost-pfs-search-suggestion-item") ||
      a.closest("li") ||
      a.closest('[class*="product"]') ||
      a.closest('[class*="Product"]') ||
      a.parentElement
    );
  };

  const findImageAnchor = (card) => {
    const img = card.querySelector("img");
    if (img) return img.closest("a, div, figure") || img.parentElement || card;
    return card;
  };

  const findPriceAnchor = (card) => {
    const byClass =
      card.querySelector('[class*="price"]') ||
      card.querySelector('[class*="Price"]') ||
      card.querySelector('[class*="money"]') ||
      card.querySelector('[class*="Money"]');

    if (byClass) return byClass;

    const els = [...card.querySelectorAll("span, div, p")];
    const hit = els.find(el => /£\s*\d/.test(el.textContent || "") && (el.textContent || "").length < 40);
    return hit || null;
  };

  // Cleanup helper (prevents badge sticking around when it shouldn't)
  const cleanupDecorations = (card) => {
    card.querySelector(".kmr-isw-compare-at")?.remove();
    card.querySelector(".kmr-isw-sale-badge")?.remove();
    card.dataset.kmrSaleDone = "0";
  };

  // ===== STOREFRONT FETCH (batched) =====
  const compareAtByHandle = new Map();
  const pending = new Set();

  const fetchMetafieldsForHandles = async (handles) => {
    const unique = [...new Set(handles)].filter(h => h && !compareAtByHandle.has(h) && !pending.has(h));
    if (!unique.length) return;

    unique.forEach(h => pending.add(h));

    const CHUNK = 15;
    for (let i = 0; i < unique.length; i += CHUNK) {
      const batch = unique.slice(i, i + CHUNK);

      const fields = batch.map((h, idx) => `
        p${idx}: product(handle: "${h.replace(/"/g, '\\"')}") {
          handle
          metafield(namespace: "${METAFIELD_NAMESPACE}", key: "${METAFIELD_KEY}") { value }
        }
      `).join("\n");

      const query = `query { ${fields} }`;

      try {
        const res = await fetch(`/api/${API_VERSION}/graphql.json`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": TOKEN
          },
          body: JSON.stringify({ query })
        });

        const json = await res.json();
        if (DEBUG) console.log("[KMR ISW] Storefront batch response:", json);

        const data = json?.data;
        if (!data) continue;

        Object.values(data).forEach((p) => {
          const handle = p?.handle;
          const raw = p?.metafield?.value;
          const num = toNumber(raw);
          if (handle && num && num > 0) compareAtByHandle.set(handle, num);
        });
      } catch (e) {
        console.error("[KMR ISW] Storefront fetch failed:", e);
      }
    }

    unique.forEach(h => pending.delete(h));
  };

  // ===== DECORATE =====
  const decorateCard = (card, handle) => {
    if (!card || card.nodeType !== 1) return;

    // If we already decorated, we still want to re-check occasionally because Boost rerenders.
    // But to keep it light, we can early exit if our elements exist already.
    // (Still safe, because cleanupDecorations removes kmrSaleDone and injected elements.)
    if (card.dataset.kmrSaleDone === "1") return;

    const compareAt = compareAtByHandle.get(handle);
    if (!compareAt) {
      cleanupDecorations(card);
      return;
    }

    const priceAnchor = findPriceAnchor(card);
    if (!priceAnchor) {
      cleanupDecorations(card);
      return;
    }

    const currentPence = getCurrentIncVatPricePence(card);
    if (!currentPence) {
      // Don't decorate yet; also ensure nothing is lingering
      cleanupDecorations(card);
      return;
    }

    const compareAtPence = Math.round(compareAt * VAT_MULTIPLIER * 100);

    // Only show if compare-at is at least £5 more than current
    if ((compareAtPence - currentPence) < 500) {
      cleanupDecorations(card);
      return;
    }

    // --- QUALIFIES: inject badge + compare-at ---

    // Badge
    const imgAnchor = findImageAnchor(card);
    imgAnchor.style.position = imgAnchor.style.position || "relative";

    if (!imgAnchor.querySelector(".kmr-isw-sale-badge")) {
      const badge = document.createElement("img");
      badge.className = "kmr-isw-sale-badge";
      badge.src = BADGE_URL;
      badge.alt = "Sale";
      imgAnchor.appendChild(badge);
    }

    // Crossed-out compare-at
    if (!priceAnchor.querySelector(".kmr-isw-compare-at")) {
      const el = document.createElement("span");
      el.className = "kmr-isw-compare-at";
      el.textContent = formatMoney(compareAtPence);
      priceAnchor.prepend(el);
    }

    card.dataset.kmrSaleDone = "1";
    if (DEBUG) console.log("[KMR ISW] Decorated:", handle, { compareAt, compareAtPence, currentPence });
  };

  // ===== SCAN =====
  let scanTimer = null;

  const scan = async () => {
    clearTimeout(scanTimer);
    scanTimer = setTimeout(async () => {
      const links = [...document.querySelectorAll('a[href*="/products/"]')]
        .filter(a => isInBoostContainer(a));

      if (DEBUG) console.log("[KMR ISW] Product links in boost:", links.length);

      const items = links.map(a => {
        const handle = getHandleFromHref(a.getAttribute("href"));
        const card = findCardForLink(a);
        return handle && card ? { handle, card } : null;
      }).filter(Boolean);

      const handles = items.map(x => x.handle);

      await fetchMetafieldsForHandles(handles);

      items.forEach(({ handle, card }) => decorateCard(card, handle));
    }, 120);
  };

  window.KMR_ISW_SALE_SCAN = scan;

  new MutationObserver(scan).observe(document.body, { childList: true, subtree: true });
  scan();
})();